import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery } from '../utils/dbUtils';
import { isBooleanTrue, isListNotEmpty, isNumberNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

class CommunityThread {
    static collectionName = 'threads';

    communityId = null;
    authorId = null;
    type = 'TEXT'; // TEXT, MEDIA, POLL, etc.

    likes = []; // array of userIds who liked
    commentCount = 0;

    pinned = false;
    status = 'ACTIVE'; // ACTIVE, ARCHIVED, DELETED

    metadata = {
        authorName: null,
        authorPhotoUrl: null
    };
    testMode = false;

    content = {
        raw: '',           // Raw text content
        richText: null,    // Serialized rich text content
        mentions: [],      // Array of { id (CommunityMember ID), display, index }
        hashtags: []       // Array of { tag, indices: [start, end] }
    };

    fileAttachments = []; // Array of { url, refId, type, name, size, metadata }

    pollId = null;        // Reference to Poll if this is a poll post
    eventId = null;       // Reference to Event if this is an event post

    reactions = {};       // Map of reaction types to arrays of userIds

    constructor(thread) {
        if (isObjectNotEmpty(thread)) {
            if (isStringNotEmpty(thread.communityId)) { this.communityId = thread.communityId; }
            if (isStringNotEmpty(thread.authorId)) { this.authorId = thread.authorId; }
            if (isStringNotEmpty(thread.type)) { this.type = thread.type; }
            if (isListNotEmpty(thread.likes)) { this.likes = thread.likes; }
            if (isNumberNotEmpty(thread.commentCount)) { this.commentCount = thread.commentCount; }
            if (isBooleanTrue(thread.pinned)) { this.pinned = thread.pinned; }
            if (isBooleanTrue(thread.status)) { this.status = thread.status; }

            if (isObjectNotEmpty(thread.metadata)) {
                this.metadata = {
                    authorName: thread.metadata.authorName || null,
                    authorPhotoUrl: thread.metadata.authorPhotoUrl || null
                };
            }

            if (isBooleanTrue(thread.testMode)) { this.testMode = true; }

            if (isObjectNotEmpty(thread.content)) {
                this.content = {
                    raw: thread?.content?.raw || '',
                    richText: thread?.content?.richText || null,
                    mentions: thread?.content?.mentions || [],
                    hashtags: thread?.content?.hashtags || []
                };
            }
            if (isListNotEmpty(thread.fileAttachments)) { this.fileAttachments = thread.fileAttachments; }
            if (isStringNotEmpty(thread.pollId)) { this.pollId = thread.pollId; }
            if (isStringNotEmpty(thread.eventId)) { this.eventId = thread.eventId; }
            if (isObjectNotEmpty(thread.reactions)) { this.reactions = thread.reactions; }
        }
    }

    toDto = () => {
        return {
            communityId: this.communityId,
            authorId: this.authorId,
            type: this.type,

            likes: this.likes,
            commentCount: this.commentCount,

            pinned: this.pinned,
            status: this.status,

            metadata: this.metadata,
            testMode: this.testMode,

            content: this.content,
            fileAttachments: this.fileAttachments,

            pollId: this.pollId,
            eventId: this.eventId,

            reactions: this.reactions
        };
    }

    static processContent(content) {
        return {
            raw: content.plainText,
            richText: content.text,
            mentions: content.mentions.map(mention => ({
                id: mention.id,
                display: mention.display,
                index: mention.index
            }))
        };
    }

    static renderContentWithMentions(content) {
        console.log('renderContentWithMentions', content);
        if (!content.mentions || content.mentions.length === 0) {
            return content.raw;
        }

        // Sort mentions by index
        const sortedMentions = [...content.mentions].sort((a, b) => a.index - b.index);

        let lastIndex = 0;
        let result = '';

        sortedMentions.forEach(mention => {
            // Add text before the mention
            result += content.raw.substring(lastIndex, mention.index);
            // Add the mention
            result += `<a href="/member/${mention.id}" class="text-blue">${mention.display}</a>`;
            // Update lastIndex to after the mention
            lastIndex = mention.index + mention.display.length; // +1 for @ symbol
        });

        // Add remaining text after last mention
        result += content.raw.substring(lastIndex);

        return result;
    }

    create = async () => {
        return await create(CommunityThread.collectionName, this.toDto());
    }

    static update = async (id, updatedThread) => {
        return await update(CommunityThread.collectionName, id, updatedThread.toDto());
    }

    static updateField = async (id, fieldName, newValue) => {
        return await updateField(CommunityThread.collectionName, id, fieldName, newValue);
    }

    static updateFields = async (id, updatedFields) => {
        return await updateFields(CommunityThread.collectionName, id, updatedFields);
    }

    static delete = async (id) => {
        return await this.updateField(id, 'status', 'DELETED');
    }

    static hardDelete = async (id) => {
        return await remove(CommunityThread.collectionName, id);
    }

    static getById = async (id) => {
        return await getById(CommunityThread.collectionName, id);
    }

    static getAll = async () => {
        return await getAll(CommunityThread.collectionName);
    }

    // Enhanced query methods
    static getByCommunityId = async (communityId, pageSize = 25, lastDoc = null) => {
        let constraints = [
            where('communityId', '==', communityId),
            where('status', '==', 'ACTIVE'),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        ];

        if (lastDoc) {
            constraints.push(startAfter(lastDoc));
        }

        const q = query(collection(db, CommunityThread.collectionName), ...constraints);
        const snapshot = await getDocs(q);
        return {
            threads: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
            lastDoc: snapshot.docs[snapshot.docs.length - 1]
        };
    }

    static getPinnedByCommunityId = async (communityId) => {
        const q = query(
            collection(db, CommunityThread.collectionName),
            where('communityId', '==', communityId),
            where('pinned', '==', true),
            where('status', '==', 'ACTIVE'),
            orderBy('createdAt', 'desc')
        );
        return await getBySingleQuery(q);
    }

    static getByAuthorId = async (authorId, pageSize = 25) => {
        const q = query(
            collection(db, CommunityThread.collectionName),
            where('authorId', '==', authorId),
            where('status', '==', 'ACTIVE'),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        );
        return await getBySingleQuery(q);
    }

    static search = async (communityId, searchText, pageSize = 25) => {
        const q = query(
            collection(db, CommunityThread.collectionName),
            where('communityId', '==', communityId),
            where('status', '==', 'ACTIVE'),
            where('content', '>=', searchText),
            where('content', '<=', searchText + '\uf8ff'),
            orderBy('content'),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        );
        return await getBySingleQuery(q);
    }

    // Thread management methods
    static pin = async (threadId) => {
        return await this.updateField(threadId, 'pinned', true);
    }

    static unpin = async (threadId) => {
        return await this.updateField(threadId, 'pinned', false);
    }

    static archive = async (threadId) => {
        return await this.updateField(threadId, 'status', 'ARCHIVED');
    }

    // Interaction methods
    static like = async (threadId, userId) => {
        const thread = await this.getById(threadId);
        if (!thread.likes.includes(userId)) {
            thread.likes.push(userId);
            return await this.updateField(threadId, 'likes', thread.likes);
        }
    }

    static unlike = async (threadId, userId) => {
        const thread = await this.getById(threadId);
        const newLikes = thread.likes.filter(id => id !== userId);
        return await this.updateField(threadId, 'likes', newLikes);
    }

    static incrementCommentCount = async (threadId) => {
        const thread = await this.getById(threadId);
        return await this.updateField(threadId, 'commentCount', (thread.commentCount || 0) + 1);
    }

    static decrementCommentCount = async (threadId) => {
        const thread = await this.getById(threadId);
        const newCount = Math.max(0, (thread.commentCount || 0) - 1);
        return await this.updateField(threadId, 'commentCount', newCount);
    }

    static addReaction = async (postId, userId, reactionType) => {
        const post = await this.getById(postId);
        if (!post.reactions[reactionType]) {
            post.reactions[reactionType] = [];
        }
        if (!post.reactions[reactionType].includes(userId)) {
            post.reactions[reactionType].push(userId);
            return await this.updateField(postId, 'reactions', post.reactions);
        }
    }

    static removeReaction = async (postId, userId, reactionType) => {
        const post = await this.getById(postId);
        if (post.reactions[reactionType]) {
            post.reactions[reactionType] = post.reactions[reactionType]
                .filter(id => id !== userId);
            return await this.updateField(postId, 'reactions', post.reactions);
        }
    }

    static processContent2 = (rawContent) => {
        const mentions = [];
        const hashtags = [];

        // Process @mentions
        const mentionRegex = /@[\w]+/g;
        let match;
        while ((match = mentionRegex.exec(rawContent)) !== null) {
            mentions.push({
                username: match[0].substring(1),
                indices: [match.index, mentionRegex.lastIndex]
            });
        }

        // Process #hashtags
        const hashtagRegex = /#[\w]+/g;
        while ((match = hashtagRegex.exec(rawContent)) !== null) {
            hashtags.push({
                tag: match[0].substring(1),
                indices: [match.index, hashtagRegex.lastIndex]
            });
        }

        return { mentions, hashtags };
    }
}

export default CommunityThread;